import { TournamentState } from '@ps/web-poker-tile-mappers';
import { Button } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback } from 'react';

import FavoriteIcon from '@swf/core/assets/icons/favorite.svg';
import { AvailableCount } from '@swf/poker';

// TODO: Use fusion icon when satellites is added to the icon library
import SatellitesIcon from '../../assets/icons/satellites.svg';
import { TournamentStateTranslations } from '../../constants';
import type { FilterVariables } from '../../types/MTT';

import {
  TXTCLI_Favorites,
  TXTCLI_Games,
  TXTCLI_Late_Registration,
  TXTCLI_Satellites
} from './TournamentStateFilter.messages.json';
import styles from './TournamentStateFilter.scss';

export const FILTER_BUTTONS: Partial<TournamentState>[] = [
  TournamentState.LATE_REGISTRATION,
  TournamentState.REGISTERING,
  TournamentState.ANNOUNCED,
  TournamentState.RUNNING,
  TournamentState.COMPLETED
];

const TRANSLATIONS: typeof TournamentStateTranslations = {
  ...TournamentStateTranslations,
  [TournamentState.LATE_REGISTRATION]: TXTCLI_Late_Registration
};

interface TournamentStateFilterProps {
  filters: FilterVariables;
  filteredCount: number;
  isFavoritesEnabled?: boolean;
  handleStateFilterClick(state: TournamentState[]): void;
  filterSatellites?: boolean;
  handleSatellitesFilterClick?(filterSatellites: boolean): void;
  handleFavoritesStatusClick(): void;
  sticky?: boolean;
  withHighlightTournaments?: boolean;
}

const TournamentStateFilter: React.FC<TournamentStateFilterProps> = ({
  filters,
  filteredCount,
  filterSatellites = false,
  handleSatellitesFilterClick,
  isFavoritesEnabled,
  handleStateFilterClick,
  handleFavoritesStatusClick,
  sticky = false,
  withHighlightTournaments = false
}) => {
  const handleClick = (state: TournamentState) => {
    const stateFilterSet = new Set(filters.state || []);

    const isRegistering = state === TournamentState.REGISTERING;
    if (stateFilterSet.has(state)) {
      stateFilterSet.delete(state);
      if (isRegistering) stateFilterSet.delete(TournamentState.PHASE);
    } else {
      stateFilterSet.add(state);
      if (isRegistering) stateFilterSet.add(TournamentState.PHASE);
    }

    handleStateFilterClick([...stateFilterSet]);
  };

  const getTrackingText = useCallback(
    (state: TournamentState) =>
      `${TRANSLATIONS[state].defaultMessage}: ${
        filters.state?.includes(state) ? 'Inactive' : 'Active'
      }`,
    [filters.state]
  );

  return (
    <div
      className={classNames(styles.root, {
        [styles.sticky]: sticky,
        [styles.stickyWithHighlightTournaments]: withHighlightTournaments
      })}
    >
      <div className={styles.states}>
        {FILTER_BUTTONS.map((state) => (
          <Button
            data-testid={`filter-button-${state}`}
            key={`${state}-button`}
            variant="primary"
            className={classNames(styles.button, {
              [styles.buttonActive]: filters.state?.includes(state)
            })}
            translation={TRANSLATIONS[state]}
            trackText={getTrackingText(state)}
            onClick={() => handleClick(state)}
          />
        ))}
        {filterSatellites && (
          <Button
            data-testid="filter-button-satellites"
            variant="primary"
            className={classNames(styles.button, {
              [styles.buttonActive]: filters.filterSatellites
            })}
            translation={TXTCLI_Satellites}
            onClick={() => handleSatellitesFilterClick?.(!filters.filterSatellites)}
            icon={() => <SatellitesIcon />}
          />
        )}
      </div>
      <div className={styles.options}>
        <Button
          variant="tertiary"
          size="small"
          className={classNames(styles.favoritesButton, {
            [styles.favoritesButtonActive]: isFavoritesEnabled
          })}
          onClick={() => handleFavoritesStatusClick()}
          icon={FavoriteIcon}
          translation={TXTCLI_Favorites}
        />
        <AvailableCount
          count={filteredCount}
          translation={TXTCLI_Games}
          className={styles.availableCount}
        />
      </div>
    </div>
  );
};

export default TournamentStateFilter;
